import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import type { PilotCapability } from '@shared';
import { useActivePilotProgram } from '@/shared/hooks/use-active-pilot-program';

export interface PilotRestrictedProps {
  children: React.ReactNode;
  capability: PilotCapability;
  side?: 'top' | 'right' | 'bottom' | 'left';
  hidden?: boolean;
  className?: string;
  tooltipText?: string;
  restricted?: boolean;
}

export function PilotRestricted({
  children,
  capability,
  side,
  hidden = false,
  className,
  tooltipText,
  restricted = false,
}: PilotRestrictedProps) {
  const program = useActivePilotProgram();
  const isCapabilityRestricted = !!program?.restrictedCapabilities.includes(capability);

  if (!restricted && !isCapabilityRestricted) {
    return <>{children}</>;
  }

  if (hidden) {
    return null;
  }

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <div className={cn('cursor-not-allowed', className)}>
          <div
            className="pointer-events-none opacity-50"
            aria-disabled="true"
            onKeyDownCapture={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onFocusCapture={e => e.stopPropagation()}
            tabIndex={-1}
          >
            {children}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className="max-w-64"
      >
        {tooltipText ?? program?.restrictedTooltip}
      </TooltipContent>
    </Tooltip>
  );
}
